import { objektNameKey, wgsNoeExtent } from '@/main/constants';
import { boundingExtent, buffer, containsCoordinate, getCenter } from 'ol/extent';
import { quickScore } from 'quick-score';
import { removeStopwords, eng, deu } from 'stopword';

/**
 * PHP's nl2br equivalent in JS
 * @param {string} str input text
 */
export function nl2br(str) {
  if (typeof str === 'undefined' || str === null) {
    return '';
  }
  const breakTag = '<br />';
  return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2');
}

/**
 * returns the full url for a given image link
 * images starting with "/img/" are local images
 * @param {string} image
 * @param {number} size size, defaults to 240 (icon size). Other Options:
 * 0: original size
 * 1920: large size
 * @returns {string}
 */
export function getUrlForImage(image, size = 240) {
  if (!image) {
    return null;
  }
  if (image.indexOf('/img/') === 0) {
    return image;
  } else {
    return `${import.meta.env.VITE_S3_BASE}/bilder/${size}/${image}`;
  }
}

/**
 *
 * @param {string} mimeType mime type (e.g. 'image/jpeg')
 * @returns {string} file ending
 */
export function getEndingForMimeType(mimeType) {
  if (mimeType === 'image/jpeg') {
    return 'jpg';
  } else if (mimeType === 'image/png') {
    return 'png';
  } else {
    return '';
  }
}

/**
 * gets the content of a cookie by name
 * @param {string} name
 * @returns
 */
export function getCookie(name) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

/**
 *
 * @param {string} flaechenbezeichnung
 * @param {number} objektart
 * @param {*} vm vue instance
 * @returns {string}
 */
export function translateFlaechenbezeichnung(flaechenbezeichnung, objektart, vm) {
  const language = vm.$i18n.locale;
  if (language !== 'de') {
    Object.values(objektNameKey).forEach(v => {
      if (flaechenbezeichnung.includes(v)) {
        flaechenbezeichnung = flaechenbezeichnung.replace(v, vm.$t(`expose.${objektart}`));
      }
    });
  }
  return flaechenbezeichnung;
}

/**
 * BEV searchable layer names:
 * 
  gst -     Grundstück
  pg  -     Politische Gemeinde
  bl  -     Bundesland
  adr -     Adresse
  gn  -     Geonams
  kg  -     Katastralgemeinde
  kg_nr -   Katastralgemeinde nummer
  kg_name - Katastralgemeinde name
  gp -      Grenzpunkt
  ez -      Einlagezahl
  rn -      Riednamen
  fpt -     Festpunkt generell
  fpt_name - Festpunkt name
  fpt_ep -   Festpunkt Einschaltpunkt
  fpt_tp -   Festpunkt Triangulierungspunkt
  pg_rgx -   Genaue suche Pol. Gemeinde
  kg_rgx -   Genaue suche Katastralgemeinde
 */

/**
 * @typedef {Object} bevSearchOptions
 * @property {Array<string>} layers array of layer BEV codes. Defaults to politische Gemeinden
 * @property {'de' | 'en'} lang language code, 'en' or 'de'. Defaults to 'de'
 */

/**
 * @typedef {Object} bevSearchResult
 * @property {number[]} extent - An array of numbers representing the extent.
 * @property {Object} properties - Additional properties of the search result.
 * @property {string} properties.name - The name of the result.
 * @property {number} properties.score - The score of the result.
 * @property {string=} properties.plz - postcode, only available for layer 'adr'
 */

/**
 * @property {string} searchString
 * @property {bevSearchOptions=} options
 * @returns {Promise<Array<bevSearchResult>>}
 */
export async function bevSearch(searchString, options = {}) {
  let filteredText = '';
  if (options.lang === 'en') {
    filteredText = removeStopwords(searchString.split(' '), eng).join(' ');
  } else {
    filteredText = removeStopwords(searchString.split(' '), deu).join(' ');
  }

  // workaround for BEV API, not finding "Sankt Pölten"
  const textWithoutSankt = filteredText
    .replace(/\b(Sankt|St\.)\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (textWithoutSankt.length > 2) {
    // make sure to start a search with only "sankt"
    filteredText = textWithoutSankt;
  }

  if (filteredText.length < 3) {
    return [];
  }
  const bevLayers = options.layers || ['pg'];

  const uri = `https://kataster.bev.gv.at/api/search/?layers=${bevLayers.join('-')}&term=${encodeURIComponent(
    filteredText
  )}`;

  try {
    const response = await fetch(uri);
    const data = await response.json();
    let features = data?.data?.features;
    if (!features?.length) {
      return [];
    }

    // only include features in NOE
    features = features.filter(f => {
      let extent;
      if (f.geometry.type === 'Point') {
        extent = buffer([...f.geometry.coordinates, ...f.geometry.coordinates], 0.0001);
      } else if (f.geometry.type === 'LineString') {
        extent = boundingExtent(f.geometry.coordinates);
      } else if (f.geometry.type === 'Polygon') {
        extent = boundingExtent(f.geometry.coordinates[0]);
      } else {
        return false;
      }
      const centerCoord = getCenter(extent);
      if (!containsCoordinate(wgsNoeExtent, centerCoord)) {
        return false;
      }
      f.extent = extent;
      delete f.geometry;
      return true;
    });

    // calculate "matching score" for each result
    features.forEach(f => {
      f.properties.score = quickScore(f.properties.name, searchString);
    });
    // sort by matching score
    features = features.sort((a, b) => {
      return b.properties.score - a.properties.score;
    });
    return features;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching BEV search results:', e);
  }
}
